import type { Vec3 } from '@rcs/domain';
import { REST_SPEED_EPSILON, SEA_LEVEL_AIR_DENSITY } from './constants';
import type { TrackPoint } from './track-lookup';

/**
 * 走行抵抗のパラメータ（docs/physics/physics-model.md 3.2・3.3 節）。SI 単位。
 * 省略した項は 0（抵抗なし）として扱う。
 */
export interface ResistanceParameters {
  /** 編成の総質量 `m` [kg]。空気抵抗の加速度への換算に使う */
  readonly mass: number;
  /** 空気抵抗係数 `C_d` */
  readonly dragCoefficient?: number;
  /** 前面投影面積 `A` [m²] */
  readonly frontalArea?: number;
  /** 転がり抵抗係数 `μ_r` */
  readonly rollingResistance?: number;
  /** 空気密度 `ρ` [kg/m³]（既定 1.225） */
  readonly airDensity?: number;
}

/**
 * 法線力を質量で割った大きさ `|f⊥| = |v² κ⃗ − g⃗ + (g⃗·T) T|` [m/s²]（物理仕様 3.3 節）。
 * 転がり抵抗の荷重 `N = m |f⊥|` に使う。曲率が大きい区間ほど大きくなる。
 *
 * @param speed 弧長方向の速度 [m/s]
 * @param gravity 重力加速度の大きさ [m/s²]
 */
export function normalLoadPerMass(point: TrackPoint, speed: number, gravity: number): number {
  const gravityDotTangent = -gravity * point.tangent[1];
  const normal: Vec3 = [
    speed * speed * point.curvature[0] + gravityDotTangent * point.tangent[0],
    speed * speed * point.curvature[1] + gravity + gravityDotTangent * point.tangent[1],
    speed * speed * point.curvature[2] + gravityDotTangent * point.tangent[2],
  ];
  return Math.hypot(normal[0], normal[1], normal[2]);
}

/**
 * 空気抵抗による接線加速度 `−½ ρ C_d A v|v| / m` [m/s²]（風は MVP では 0）。
 */
export function dragAcceleration(parameters: ResistanceParameters, speed: number): number {
  const {
    mass,
    dragCoefficient = 0,
    frontalArea = 0,
    airDensity = SEA_LEVEL_AIR_DENSITY,
  } = parameters;
  if (dragCoefficient === 0 || frontalArea === 0) return 0;
  return (-0.5 * airDensity * dragCoefficient * frontalArea * speed * Math.abs(speed)) / mass;
}

/**
 * 転がり抵抗による接線加速度 `−μ_r |f⊥| · sgn(v)` [m/s²]。
 *
 * 速度がほぼ 0 のときは、重力の接線成分 `slopeAcceleration` の向きへすべる向きに抗する。
 *
 * @param slopeAcceleration 重力の接線成分による加速度 [m/s²]
 */
export function rollingAcceleration(
  parameters: ResistanceParameters,
  point: TrackPoint,
  speed: number,
  slopeAcceleration: number,
  gravity: number,
): number {
  const mu = parameters.rollingResistance ?? 0;
  if (mu === 0) return 0;
  const direction =
    Math.abs(speed) >= REST_SPEED_EPSILON ? Math.sign(speed) : Math.sign(slopeAcceleration);
  return -mu * normalLoadPerMass(point, speed, gravity) * direction;
}

/**
 * 静止保持の判定（物理仕様 5 章）。ほぼ静止していて、重力の接線成分が最大静止摩擦
 * （転がり抵抗で代用：`μ_r |f⊥|`）以下なら、車両は動かない。
 */
export function holdsAtRest(
  parameters: ResistanceParameters,
  point: TrackPoint,
  slopeAcceleration: number,
  gravity: number,
): boolean {
  const mu = parameters.rollingResistance ?? 0;
  if (mu === 0) return false;
  return Math.abs(slopeAcceleration) <= mu * normalLoadPerMass(point, 0, gravity);
}
